import React, { useState, useEffect, useRef } from 'react';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, X, Star, MessageCircle, Shield, MapPin, 
  ChevronLeft, Settings, Sparkles, Check, Dog,
  Calendar, Info, RefreshCw, Zap, LogIn, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fallback demo dogs (shown when not logged in or no dogs in DB)
const DEMO_DOGS = [
  {
    id: '1',
    name: 'Bella',
    age: 2,
    breed: 'Mini Pomeranian',
    distance: 2.3,
    bio: 'Playful princess looking for her prince! Loves cuddles, beach walks, and stealing socks.',
    photos: ['https://images.pexels.com/photos/1458924/pexels-photo-1458924.jpeg?auto=compress&cs=tinysrgb&w=800'],
    verified: true,
    lookingFor: 'breeding',
    personality: ['Playful', 'Friendly', 'Energetic'],
    owner: 'Sarah M.',
    vaccinated: true,
  },
  {
    id: '2', 
    name: 'Max',
    age: 3,
    breed: 'Golden Retriever',
    distance: 5.1,
    bio: 'Big golden boy with an even bigger heart. Champion bloodline, health tested.',
    photos: ['https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=800'],
    verified: true,
    lookingFor: 'breeding',
    personality: ['Gentle', 'Loyal', 'Smart'],
    owner: 'Mike T.',
    vaccinated: true,
  },
  {
    id: '3',
    name: 'Luna',
    age: 1,
    breed: 'French Bulldog',
    distance: 3.7,
    bio: 'Snorty little lady seeking playmates! I might be small but I have BIG personality.',
    photos: ['https://images.pexels.com/photos/4587998/pexels-photo-4587998.jpeg?auto=compress&cs=tinysrgb&w=800'],
    verified: true,
    lookingFor: 'playdate',
    personality: ['Cheeky', 'Cuddly', 'Lazy'],
    owner: 'Emma L.',
    vaccinated: true,
  },
  {
    id: '4',
    name: 'Charlie',
    age: 4,
    breed: 'Border Collie',
    distance: 8.2,
    bio: 'Smartest pup on the block! Agility champion looking for equally active friends.',
    photos: ['https://images.pexels.com/photos/551628/pexels-photo-551628.jpeg?auto=compress&cs=tinysrgb&w=800'],
    verified: false,
    lookingFor: 'playdate',
    personality: ['Athletic', 'Smart', 'Focused'],
    owner: 'James K.',
    vaccinated: true,
  },
  {
    id: '5',
    name: 'Coco',
    age: 2,
    breed: 'Cavoodle',
    distance: 1.5,
    bio: 'Fluffy cloud of happiness! Love park adventures and making new furry friends.',
    photos: ['https://images.pexels.com/photos/5732524/pexels-photo-5732524.jpeg?auto=compress&cs=tinysrgb&w=800'],
    verified: true,
    lookingFor: 'both',
    personality: ['Sweet', 'Social', 'Cuddly'],
    owner: 'Lisa R.',
    vaccinated: true,
  },
  {
    id: '6',
    name: 'Rocky',
    age: 5,
    breed: 'German Shepherd',
    distance: 12.4,
    bio: 'Strong, loyal, and protective. Looking for a worthy partner for beautiful pups.',
    photos: ['https://images.pexels.com/photos/333083/pexels-photo-333083.jpeg?auto=compress&cs=tinysrgb&w=800'],
    verified: true,
    lookingFor: 'breeding',
    personality: ['Protective', 'Loyal', 'Brave'],
    owner: 'David H.',
    vaccinated: true,
  },
];

// Swipeable Card Component
const SwipeCard = ({ dog, onSwipe, isTop }) => {
  const cardRef = useRef(null);
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-25, 25]);
  const opacity = useTransform(x, [-200, -100, 0, 100, 200], [0.5, 1, 1, 1, 0.5]);
  
  // Like/Nope indicators
  const likeOpacity = useTransform(x, [0, 100], [0, 1]);
  const nopeOpacity = useTransform(x, [-100, 0], [1, 0]);
  
  const [showInfo, setShowInfo] = useState(false);

  const handleDragEnd = (_, info) => {
    if (info.offset.x > 100) {
      onSwipe('like', dog);
    } else if (info.offset.x < -100) {
      onSwipe('pass', dog);
    }
  };

  if (!isTop) {
    return (
      <motion.div
        className="absolute w-full h-full"
        style={{ scale: 0.95, y: 10 }}
      >
        <div className="w-full h-full bg-white rounded-3xl shadow-lg overflow-hidden">
          <img 
            src={dog.photos[0]} 
            alt={dog.name}
            className="w-full h-full object-cover opacity-50"
          />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      ref={cardRef}
      className="absolute w-full h-full cursor-grab active:cursor-grabbing"
      style={{ x, rotate, opacity }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.9}
      onDragEnd={handleDragEnd}
      whileTap={{ scale: 1.02 }}
    >
      <div className="relative w-full h-full bg-white rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.15)] overflow-hidden">
        {/* Photo */}
        <img 
          src={dog.photos[0]} 
          alt={dog.name}
          className="w-full h-full object-cover"
        />
        
        {/* Like Indicator */}
        <motion.div 
          className="absolute top-8 left-8 bg-[#4E876C] text-white px-6 py-2 rounded-xl font-bold text-2xl rotate-[-20deg] border-4 border-white shadow-lg"
          style={{ opacity: likeOpacity }}
        >
          LIKE
        </motion.div>
        
        {/* Nope Indicator */}
        <motion.div 
          className="absolute top-8 right-8 bg-[#FF6B6B] text-white px-6 py-2 rounded-xl font-bold text-2xl rotate-[20deg] border-4 border-white shadow-lg"
          style={{ opacity: nopeOpacity }}
        >
          NOPE
        </motion.div>
        
        {/* Verified Badge */}
        {dog.verified && (
          <div className="absolute top-4 right-4 bg-[#4E876C] text-white px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 shadow-lg">
            <Shield className="w-4 h-4" />
            Vet Verified
          </div>
        )}
        
        {/* Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6">
          {/* Looking For Badge */}
          <div className="flex gap-2 mb-3">
            {dog.lookingFor === 'breeding' && (
              <span className="bg-[#FF7555] text-white text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1">
                <Heart className="w-3 h-3" /> Looking for Breeding
              </span>
            )}
            {dog.lookingFor === 'playdate' && (
              <span className="bg-[#F4D35E] text-black text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Looking for Playdate
              </span>
            )}
            {dog.lookingFor === 'both' && (
              <span className="bg-[#9B6B9E] text-white text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1">
                <Star className="w-3 h-3" /> Open to Both
              </span>
            )}
          </div>
          
          {/* Name & Basic Info */}
          <div className="flex items-end justify-between">
            <div>
              <h2 className="text-white text-3xl font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                {dog.name}, {dog.age}
              </h2>
              <p className="text-white/80 text-lg">{dog.breed}</p>
              <div className="flex items-center gap-4 mt-2 text-white/70 text-sm">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {dog.distance}km away
                </span>
                {dog.vaccinated && (
                  <span className="flex items-center gap-1">
                    <Check className="w-4 h-4 text-[#4E876C]" />
                    Vaccinated
                  </span>
                )}
              </div>
            </div>
            <button 
              onClick={() => setShowInfo(!showInfo)}
              className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center hover:bg-white/30 transition"
            >
              <Info className="w-5 h-5 text-white" />
            </button>
          </div>
          
          {/* Expanded Info */}
          <AnimatePresence>
            {showInfo && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-4 pt-4 border-t border-white/20">
                  <p className="text-white/90 text-sm mb-3">{dog.bio}</p>
                  <div className="flex flex-wrap gap-2">
                    {dog.personality.map((trait, idx) => (
                      <span key={idx} className="bg-white/20 text-white text-xs px-3 py-1 rounded-full">
                        {trait}
                      </span>
                    ))}
                  </div>
                  <p className="text-white/60 text-xs mt-3">Owner: {dog.owner}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

// Match Popup Component
const MatchPopup = ({ dog, onClose, onMessage, t }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.8, y: 50 }}
        className="bg-white rounded-3xl p-8 max-w-sm w-full text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring' }}
          className="w-24 h-24 bg-gradient-to-br from-[#FF7555] to-[#E5684A] rounded-full mx-auto mb-4 flex items-center justify-center"
        >
          <Heart className="w-12 h-12 text-white" fill="white" />
        </motion.div>
        
        <h2 className="text-3xl font-bold text-[#1A1817] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
          {t('pupMatch.match.title')} 🎉
        </h2>
        <p className="text-[#7A7571] mb-6">
          {t('pupMatch.match.subtitle', { name: dog.name })}
        </p>
        
        <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-6 ring-4 ring-[#FF7555]">
          <img src={dog.photos[0]} alt={dog.name} className="w-full h-full object-cover" />
        </div>
        
        <div className="flex gap-3">
          <Button
            onClick={onMessage}
            className="flex-1 bg-[#FF7555] hover:bg-[#E5684A] text-white h-12 rounded-xl font-semibold"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            {t('pupMatch.match.sendMessage')}
          </Button>
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1 h-12 rounded-xl font-semibold border-[#EBE8E0]"
          >
            {t('pupMatch.match.keepSwiping')}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Pup Match Component
export default function PupMatch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dogs, setDogs] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [matches, setMatches] = useState([]);
  const [showMatch, setShowMatch] = useState(null);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [selectedDogId, setSelectedDogId] = useState(null); // User's dog doing the swiping

  const currentDog = dogs[currentIndex];
  const nextDog = dogs[currentIndex + 1];

  // Check auth and fetch dogs
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      
      // Check if user is logged in
      try {
        const authRes = await fetch(`${API_URL}/api/puppoto/auth/me`, {
          credentials: 'include'
        });
        
        if (authRes.ok) {
          const userData = await authRes.json();
          setUser(userData);
          
          // Get user's dogs to select which one is swiping
          const myDogsRes = await fetch(`${API_URL}/api/puppoto/my-dogs`, {
            credentials: 'include'
          });
          if (myDogsRes.ok) {
            const myDogsData = await myDogsRes.json();
            if (myDogsData.dogs?.length > 0) {
              setSelectedDogId(myDogsData.dogs[0].id); // Default to first dog
            }
          }
          
          // Fetch dogs to discover
          const discoverRes = await fetch(`${API_URL}/api/puppoto/discover?limit=20`, {
            credentials: 'include'
          });
          
          if (discoverRes.ok) {
            const discoverData = await discoverRes.json();
            if (discoverData.dogs?.length > 0) {
              // Transform API data to match component format
              const transformedDogs = discoverData.dogs.map(dog => ({
                ...dog,
                distance: dog.location?.suburb ? `${dog.location.suburb}` : 'Nearby',
                lookingFor: dog.looking_for,
                owner: dog.owner_name || 'Dog Lover',
                photos: dog.photos?.length > 0 ? dog.photos : [
                  'https://images.pexels.com/photos/1805164/pexels-photo-1805164.jpeg?auto=compress&cs=tinysrgb&w=800'
                ]
              }));
              setDogs(transformedDogs);
            } else {
              // No dogs in DB, show demo
              setDogs(DEMO_DOGS);
            }
          } else {
            setDogs(DEMO_DOGS);
          }
        } else {
          // Not logged in - show demo dogs
          setDogs(DEMO_DOGS);
        }
      } catch (error) {
        console.error('Init error:', error);
        setDogs(DEMO_DOGS);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, []);

  const handleSwipe = async (direction, dog) => {
    setSwipeDirection(direction);
    
    // Record swipe to backend if logged in
    if (user && selectedDogId) {
      try {
        const res = await fetch(`${API_URL}/api/puppoto/match/swipe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            from_dog_id: selectedDogId,
            to_dog_id: dog.id,
            action: direction
          })
        });
        
        if (res.ok) {
          const data = await res.json();
          if (data.is_match) {
            // It's a real match!
            setTimeout(() => {
              setShowMatch(dog);
              setMatches(prev => [...prev, dog]);
            }, 300);
          }
        }
      } catch (error) {
        console.error('Swipe error:', error);
      }
    } else {
      // Demo mode - simulate match (30% chance)
      if (direction === 'like' && Math.random() > 0.7) {
        setTimeout(() => {
          setShowMatch(dog);
          setMatches(prev => [...prev, dog]);
        }, 300);
      }
    }
    
    // Show toast
    if (direction === 'like') {
      toast.success(t('pupMatch.liked', { name: dog.name }));
    } else if (direction === 'superlike') {
      toast.success(t('pupMatch.superLiked', { name: dog.name }));
    }
    
    // Move to next card
    setTimeout(() => {
      setCurrentIndex(prev => prev + 1);
      setSwipeDirection(null);
    }, 200);
  };

  const handleButtonSwipe = (direction) => {
    if (currentDog) {
      handleSwipe(direction, currentDog);
    }
  };

  const resetDeck = async () => {
    setCurrentIndex(0);
    
    // If logged in, refetch dogs
    if (user) {
      try {
        const discoverRes = await fetch(`${API_URL}/api/puppoto/discover?limit=20`, {
          credentials: 'include'
        });
        if (discoverRes.ok) {
          const data = await discoverRes.json();
          if (data.dogs?.length > 0) {
            const transformedDogs = data.dogs.map(dog => ({
              ...dog,
              distance: dog.location?.suburb ? `${dog.location.suburb}` : 'Nearby',
              lookingFor: dog.looking_for,
              owner: dog.owner_name || 'Dog Lover',
              photos: dog.photos?.length > 0 ? dog.photos : [
                'https://images.pexels.com/photos/1805164/pexels-photo-1805164.jpeg?auto=compress&cs=tinysrgb&w=800'
              ]
            }));
            setDogs(transformedDogs);
          }
        }
      } catch (error) {
        console.error('Refresh error:', error);
      }
    }
    
    toast.success('Deck refreshed! 🔄');
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/puppoto/match';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Check if we've run out of dogs
  const noMoreDogs = currentIndex >= dogs.length;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Figtree', sans-serif" }}>
      {/* Subtle texture */}
      <div className="fixed inset-0 opacity-[0.02] pointer-events-none" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
      }} />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#EBE8E0]">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/puppoto" className="flex items-center gap-2">
            <ChevronLeft className="w-5 h-5 text-[#7A7571]" />
          </a>
          
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-[#FF7555] to-[#E5684A] rounded-xl flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              {t('pupMatch.title')}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="compact" />
            {user ? (
              <button 
                onClick={() => navigate('/puppoto/profile')}
                className="w-10 h-10 rounded-full bg-[#F5F3EE] flex items-center justify-center overflow-hidden"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-5 h-5 text-[#7A7571]" />
                )}
              </button>
            ) : (
              <button 
                onClick={handleGoogleLogin}
                className="w-10 h-10 rounded-full bg-[#FF7555] flex items-center justify-center"
              >
                <LogIn className="w-5 h-5 text-white" />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Demo Mode Banner */}
      {!user && !isLoading && (
        <div className="bg-[#F4D35E]/20 border-b border-[#F4D35E]/30 px-4 py-2">
          <p className="text-center text-sm text-[#7A7571]">
            <Sparkles className="w-4 h-4 inline mr-1" />
            Demo mode - <button onClick={handleGoogleLogin} className="text-[#FF7555] font-semibold underline">Sign in</button> to save matches!
          </p>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-lg mx-auto px-4 py-6">
        {/* Loading State */}
        {isLoading ? (
          <div className="h-[500px] flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#FF7555] to-[#E5684A] rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
                <Dog className="w-8 h-8 text-white" />
              </div>
              <p className="text-[#7A7571]">Finding pups near you...</p>
            </div>
          </div>
        ) : (
        <>
        {/* Card Stack */}
        <div className="relative h-[500px] mb-8">
          {noMoreDogs ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute inset-0 flex flex-col items-center justify-center text-center p-8"
            >
              <div className="w-24 h-24 bg-[#F5F3EE] rounded-full flex items-center justify-center mb-6">
                <Dog className="w-12 h-12 text-[#7A7571]" />
              </div>
              <h3 className="text-2xl font-bold text-[#1A1817] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                {t('pupMatch.noMoreDogs.title')}
              </h3>
              <p className="text-[#7A7571] mb-6">
                {t('pupMatch.noMoreDogs.subtitle')}
              </p>
              <Button
                onClick={resetDeck}
                className="bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl h-12 px-6"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                {t('pupMatch.noMoreDogs.refresh')}
              </Button>
            </motion.div>
          ) : (
            <AnimatePresence>
              {nextDog && (
                <SwipeCard 
                  key={nextDog.id}
                  dog={nextDog} 
                  onSwipe={handleSwipe}
                  isTop={false}
                />
              )}
              {currentDog && (
                <SwipeCard 
                  key={currentDog.id}
                  dog={currentDog} 
                  onSwipe={handleSwipe}
                  isTop={true}
                />
              )}
            </AnimatePresence>
          )}
        </div>

        {/* Action Buttons */}
        {!noMoreDogs && (
          <div className="flex items-center justify-center gap-4">
            {/* Pass Button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => handleButtonSwipe('pass')}
              className="w-16 h-16 bg-white rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.1)] flex items-center justify-center border-2 border-[#EBE8E0] hover:border-[#FF6B6B] transition-colors group"
              data-testid="pup-match-pass-button"
            >
              <X className="w-8 h-8 text-[#FF6B6B] group-hover:scale-110 transition-transform" />
            </motion.button>
            
            {/* Super Like Button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => handleButtonSwipe('superlike')}
              className="w-14 h-14 bg-[#F4D35E] rounded-full shadow-[0_4px_20px_rgba(244,211,94,0.4)] flex items-center justify-center hover:bg-[#E5C44F] transition-colors"
              data-testid="pup-match-superlike-button"
            >
              <Star className="w-7 h-7 text-white" fill="white" />
            </motion.button>
            
            {/* Like Button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => handleButtonSwipe('like')}
              className="w-16 h-16 bg-[#4E876C] rounded-full shadow-[0_4px_20px_rgba(78,135,108,0.4)] flex items-center justify-center hover:bg-[#3D6B56] transition-colors"
              data-testid="pup-match-like-button"
            >
              <Heart className="w-8 h-8 text-white" fill="white" />
            </motion.button>
          </div>
        )}

        {/* Matches Preview */}
        {matches.length > 0 && (
          <div className="mt-8 bg-white rounded-2xl p-4 shadow-[0_4px_20px_rgba(0,0,0,0.05)]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[#1A1817] flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#FF7555]" />
                {t('pupMatch.yourMatches', { count: matches.length })}
              </h3>
              <a href="#" className="text-sm text-[#FF7555] font-medium">{t('common.viewAll')}</a>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {matches.map((dog) => (
                <div key={dog.id} className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full overflow-hidden ring-2 ring-[#FF7555] ring-offset-2">
                    <img src={dog.photos[0]} alt={dog.name} className="w-full h-full object-cover" />
                  </div>
                  <p className="text-xs text-center mt-1 text-[#7A7571]">{dog.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tips */}
        <div className="mt-6 text-center">
          <p className="text-sm text-[#7A7571]">
            💡 <span className="font-medium">{t('common.learnMore').split(' ')[0]}:</span> {t('pupMatch.tip')}
          </p>
        </div>
        </>
        )}
      </main>

      {/* Match Popup */}
      <AnimatePresence>
        {showMatch && (
          <MatchPopup
            dog={showMatch}
            onClose={() => setShowMatch(null)}
            onMessage={() => {
              toast.success('Opening chat...');
              setShowMatch(null);
            }}
            t={t}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
